import * as React from "react";
import { cn } from "../../lib/utils";
import { Text } from "../Text";
import { IconButton } from "../IconButton";
import { Divider } from "../Divider";

export interface ModalProps {
  /**
   * Whether the modal is open
   */
  isOpen: boolean;
  /**
   * Callback when the modal should close
   */
  onClose?: () => void;
  /**
   * Modal title - optional
   */
  title?: string;
  /**
   * Modal description/subtitle - optional
   */
  description?: string;
  /**
   * Modal footer content - optional
   */
  footer?: React.ReactNode;
  /**
   * Modal body content
   */
  children: React.ReactNode;
  /**
   * Size variant
   */
  size?: "small" | "medium" | "large" | "xlarge";
  /**
   * Whether to show the close button in header
   */
  showCloseButton?: boolean;
  /**
   * Whether clicking the overlay closes the modal
   */
  closeOnOverlayClick?: boolean;
  /**
   * Whether pressing Escape closes the modal
   */
  closeOnEscape?: boolean;
  /**
   * Custom class name for the modal container
   */
  className?: string;
  /**
   * Custom class name for the modal content
   */
  contentClassName?: string;
  /**
   * Custom class name for the header
   */
  headerClassName?: string;
  /**
   * Custom class name for the body
   */
  bodyClassName?: string;
  /**
   * Custom class name for the footer
   */
  footerClassName?: string;
  /**
   * Custom class name for the overlay
   */
  overlayClassName?: string;
  /**
   * ARIA label for accessibility
   */
  ariaLabel?: string;
  /**
   * ARIA description for accessibility
   */
  ariaDescribedBy?: string;
}

const Modal = React.forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      isOpen,
      onClose,
      title,
      description,
      footer,
      children,
      size = "medium",
      showCloseButton = true,
      closeOnOverlayClick = true,
      closeOnEscape = true,
      className,
      contentClassName,
      headerClassName,
      bodyClassName,
      footerClassName,
      overlayClassName,
      ariaLabel,
      ariaDescribedBy,
    },
    ref
  ) => {
    const modalRef = React.useRef<HTMLDivElement>(null);
    const contentRef = ref || modalRef;

    // Size configurations
    const sizeConfig = {
      small: "max-w-sm",
      medium: "max-w-md",
      large: "max-w-lg",
      xlarge: "max-w-2xl",
    };

    // Handle escape key
    React.useEffect(() => {
      if (!isOpen || !closeOnEscape || !onClose) return;

      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          onClose();
        }
      };

      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }, [isOpen, closeOnEscape, onClose]);

    // Prevent body scroll when modal is open
    React.useEffect(() => {
      if (isOpen) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }

      return () => {
        document.body.style.overflow = "";
      };
    }, [isOpen]);

    // Handle overlay click
    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (closeOnOverlayClick && e.target === e.currentTarget && onClose) {
        onClose();
      }
    };

    // Don't render if not open
    if (!isOpen) return null;

    const hasHeader = title || description;

    return (
      <div
        className={cn(
          "fixed inset-0 z-50 flex items-center justify-center p-4",
          className
        )}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel || title}
        aria-describedby={ariaDescribedBy}
      >
        {/* Overlay */}
        <div
          className={cn(
            "absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity",
            overlayClassName
          )}
          onClick={handleOverlayClick}
          aria-hidden="true"
        />

        {/* Modal Content */}
        <div
          ref={contentRef}
          className={cn(
            "relative w-full bg-white rounded-large shadow-xl transition-all",
            "flex flex-col max-h-[90vh]",
            sizeConfig[size],
            contentClassName
          )}
        >
          {/* Header */}
          {hasHeader && (
            <div
              className={cn(
                "flex items-start justify-between gap-4 px-6 pt-6",
                !description && "pb-4",
                description && "pb-2",
                headerClassName
              )}
            >
              <div className="flex-1">
                {title && (
                  <Text
                    as="h2"
                    variant="body"
                    size="large"
                    weight="semibold"
                    color="default"
                  >
                    {title}
                  </Text>
                )}
                {description && (
                  <Text
                    as="p"
                    variant="body"
                    size="small"
                    weight="regular"
                    color="subtle"
                    className="mt-1"
                  >
                    {description}
                  </Text>
                )}
              </div>

              {showCloseButton && onClose && (
                <IconButton
                  icon="close"
                  onClick={onClose}
                  color="neutral"
                  size="small"
                  aria-label="Close modal"
                  className="shrink-0"
                />
              )}
            </div>
          )}

          {/* Header with close button only (no title/description) */}
          {!hasHeader && showCloseButton && onClose && (
            <div className="absolute top-4 right-4 z-10">
              <IconButton
                icon="close"
                onClick={onClose}
                color="neutral"
                size="small"
                aria-label="Close modal"
              />
            </div>
          )}

          {/* Body */}
          <div
            className={cn(
              "flex-1 overflow-y-auto px-6",
              hasHeader ? "py-4" : "pt-6 pb-4",
              !footer && "pb-6",
              bodyClassName
            )}
          >
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <div className="flex flex-col">
              <Divider thickness="thin" variant="muted" />
              <div
                className={cn(
                  "flex items-center justify-end gap-3 px-6 py-4",
                  footerClassName
                )}
              >
                {footer}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
);

Modal.displayName = "Modal";

export { Modal };
